/**
 * Logique pure des raccourcis clavier et des bornes de zoom du visualiseur de
 * pièces (InlineFileViewer), extraite pour être testable sans DOM.
 */

export type ViewerKeyAction = 'prev' | 'next' | 'rotateLeft' | 'rotateRight' | 'zoomIn' | 'zoomOut' | 'zoomReset';

export interface ViewerKeyInput {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  /** tagName de l'élément focusé (ex: 'INPUT'), si connu. */
  targetTagName?: string;
  /** true si l'élément focusé est contentEditable. */
  targetIsEditable?: boolean;
}

/**
 * Action visualiseur correspondant à un keydown, ou null si l'événement ne
 * doit pas être intercepté.
 */
export const viewerKeyAction = (e: ViewerKeyInput): ViewerKeyAction | null => {
  // Cmd/Ctrl/Alt = raccourci navigateur ou système (Cmd+- zoome la PAGE,
  // Cmd+R recharge…) : le visualiseur ne doit PAS réagir en plus, sinon un
  // Cmd+- réduit silencieusement la pièce à 50 % de sa zone et laisse un
  // grand vide autour (constaté en review /cg/:id/review).
  const hasSystemModifier = e.metaKey || e.ctrlKey || e.altKey;
  if (hasSystemModifier) return null;

  // Focus dans un champ de saisie (édition des champs à droite) : ignorer.
  const isTypingTarget = e.targetTagName === 'INPUT' || e.targetTagName === 'TEXTAREA' || e.targetIsEditable === true;
  if (isTypingTarget) return null;

  if (e.key === 'ArrowLeft') return 'prev';
  if (e.key === 'ArrowRight') return 'next';
  if (e.key === 'r' || e.key === 'R') return e.shiftKey ? 'rotateLeft' : 'rotateRight';
  if (e.key === '+' || e.key === '=') return 'zoomIn';
  if (e.key === '-' || e.key === '_') return 'zoomOut';
  if (e.key === '0') return 'zoomReset';
  return null;
};

export const MAX_VIEWER_ZOOM = 4;

/**
 * Zoom minimal du visualiseur selon le type de pièce. Un PDF est rendu dans un
 * iframe dont le lecteur natif (Chrome/Firefox) gère déjà son propre fit :
 * dézoomer sous 100 % ne fait que rétrécir tout le lecteur au centre de la
 * zone (grand vide autour, pièce illisible). On verrouille donc à 100 % pour
 * les PDF ; les images gardent un plancher à 50 % (utile pour un scan pivoté
 * qui déborde de la zone).
 */
export const minViewerZoom = (isPdfFile: boolean): number => (isPdfFile ? 1 : 0.5);
